import {
  IGraphDataPoints, IGraphYAxis, IScanStats, IStat,
} from '../analytics';
import { IBodyScanHistory } from '../bodyscan';
import {
  Allergens, DayOfWeek, Meal, MealIngredient,
  MealOfDay, MealPlanWithMeals, ShoppingItem, Unit,
} from '../nutrition';
export enum MassUnit {
  Killogram = 'kg',
  Pound = 'lb',
  Feet = 'ft',
  Centimeter = 'cm',
  Inches = 'In',
}

export enum UnitPref {
  imperial = 'imperial',
  metric = 'metric',
}

export type NutritionalEnergyUnit = 'cals' | 'kj';

export class MetricToImperialConverter {
  public static convertAmount(
    fromMeasurement: Unit | MassUnit | NutritionalEnergyUnit,
    toMeasurement: Unit | MassUnit | NutritionalEnergyUnit,
    units: number,
  ): number {
    if (fromMeasurement === Unit.Gram && toMeasurement === Unit.Ounce) {
      return this.round(units * 0.035274, 2);
    }
    if (fromMeasurement === Unit.Milliliter && toMeasurement === Unit.FluidOunce) {
      return this.round(units * 0.0351951, 2);
    }
    if (fromMeasurement === MassUnit.Killogram && toMeasurement === MassUnit.Pound) {
      return this.round(units * 2.20462, 1);
    }
    if (fromMeasurement === MassUnit.Feet && toMeasurement === MassUnit.Centimeter) {
      return this.round(units * 30.48, 0);
    }
    if (fromMeasurement === MassUnit.Centimeter && toMeasurement === MassUnit.Feet) {
      return this.round(units * 0.0328084, 2);
    }
    if (fromMeasurement === MassUnit.Pound && toMeasurement === MassUnit.Killogram) {
      return this.round(units * 0.453592, 2);
    }
    if (fromMeasurement === MassUnit.Feet && toMeasurement === MassUnit.Inches) {
      return this.round(units * 12, 0);
    }
    if (fromMeasurement === 'cals' && toMeasurement === 'kj') {
      return this.round(units * 4.1868, 2);
    }
    if (fromMeasurement === 'kj' && toMeasurement === 'cals') {
      return this.round(units / 4.1868, 0);
    }

    throw new Error(`Unable to convert from ${fromMeasurement} to ${toMeasurement}`);
  }

  public static convertMealIngredientUnitToImperial(unit: Unit): Unit {
    if (unit === Unit.Unit || unit === Unit.TeaSpoon) {
      return unit;
    }
    switch (unit) {
      case Unit.Gram:
        return Unit.Ounce;
      case Unit.Milliliter:
        return Unit.FluidOunce;
      default:
        throw new Error(`Unable to convert ${unit} to imperial`);
    }
  }

  public static convertDailyMealMacroCountToImperial(mealPlanWithMeals: MealPlanWithMeals) {
    const convertSumToImperial = (macros: Map<DayOfWeek, number>) => {
      for (const dayOfWeek of Object.keys(DayOfWeek)) {
        if (macros.get(DayOfWeek[String(dayOfWeek)])) {
          macros.set(
            DayOfWeek[String(dayOfWeek)],
            this.convertAmount(Unit.Gram, Unit.Ounce, macros.get(DayOfWeek[dayOfWeek]) as number),
          );
        }
      }
    };
    convertSumToImperial(mealPlanWithMeals.fatForDay);
    convertSumToImperial(mealPlanWithMeals.carbsForDay);
    convertSumToImperial(mealPlanWithMeals.proteinForDay);
  }

  public static convertIndividuLMealMacroCountToImperial(
    mealPlanWithMeals: MealPlanWithMeals,
    allIngredients: MealIngredient[],
  ) {
    for (const dayOfWeek of Object.keys(DayOfWeek)) {
      for (const mealOfDay of Object.keys(MealOfDay)) {
        const mealOfDayMap = mealPlanWithMeals.mealSchedule.get(DayOfWeek[String(dayOfWeek)]) as Map<MealOfDay, Meal>;
        if (mealOfDayMap) {
          const meal = mealOfDayMap.get(MealOfDay[String(mealOfDay)]) as Meal;
          if (meal) {
            meal.fat = Meal.Fat(meal, allIngredients, true);
            meal.carbs = Meal.Carbs(meal, allIngredients, true);
            meal.protein = Meal.Protein(meal, allIngredients, true);
          }
        }
      }
    }
  }

  public static convertCentimetersToFeetAndInches(centimeters: number) {
    const totalFeet = this.convertAmount(MassUnit.Centimeter, MassUnit.Feet, centimeters);
    return {
      feet: Math.floor(totalFeet / 1).toString(),
      inches: this.convertAmount(MassUnit.Feet, MassUnit.Inches, totalFeet % 1).toString(),
    };
  }

  public static convertInchesAndFeetToCentimeters(parsedHeightObj: { [key: string]: string }) {
    const totalFeet = this.convertInchesAndFeetToTotalFeet(parsedHeightObj);
    return this.convertAmount(
      MassUnit.Feet,
      MassUnit.Centimeter,
      totalFeet,
    );
  }

  public static convertInchesAndFeetToFeet(parsedHeightObj: { [key: string]: string }) {
    return this.convertInchesAndFeetToTotalFeet(parsedHeightObj);
  }

    public static convertMealsIngredientsDescriptionImperial(
      mealPlanWithMeals: MealPlanWithMeals,
      allIngredients: MealIngredient[],
      allergens: Allergens[],
    ) {
      for (const dayOfWeek of Object.keys(DayOfWeek)) {
      for (const mealOfDay of Object.keys(MealOfDay)) {
        const mealOfDayMap = mealPlanWithMeals.mealSchedule.get(DayOfWeek[String(dayOfWeek)]) as Map<MealOfDay, Meal>;
        if (mealOfDayMap) {
          const meal = mealOfDayMap.get(MealOfDay[String(mealOfDay)]) as Meal;
          if (meal) {
            Meal.setIngredientDescriptions(meal, allIngredients, allergens, true);
          }
        }
      }
    }
  }

  public static convertShoppingListToImperial(shoppingList: ShoppingItem[]): ShoppingItem[] {
    return shoppingList.map((item: ShoppingItem) => {
      if (item.unit === Unit.Unit || item.unit === Unit.TeaSpoon) {
        return item;
      }
      const imperialUnit = this.convertMealIngredientUnitToImperial(item.unit);
      return {
        ...item,
        unit: imperialUnit,
        quantity: this.convertAmount(item.unit, imperialUnit, item.quantity),
      };
    });
  }

  public static convertBodyScanHistoryToImperial(bodyScanHistoryInMetric: IBodyScanHistory[]): IBodyScanHistory[] {
    const bodyScanHistoryInImperial = bodyScanHistoryInMetric.map((bodyScanItem) => ({
      ...bodyScanItem,
      muscleMassMeterValue: this.convertAmount(MassUnit.Killogram, MassUnit.Pound, bodyScanItem.muscleMassMeterValue),
      muscleMassMeterKg: this.convertAmount(MassUnit.Killogram, MassUnit.Pound, bodyScanItem.muscleMassMeterValue),
      weight: this.convertAmount(MassUnit.Killogram, MassUnit.Pound, bodyScanItem.weight),
      unit: MassUnit.Pound,
    }));

    return bodyScanHistoryInImperial;
  }

  public static convertScanStatsToImperial(metricScanStats: IScanStats): IScanStats {
    const imperialScanStats = { ...metricScanStats };
    imperialScanStats.muscleMass = this.convertMetricStatToImperial(imperialScanStats.muscleMass);
    imperialScanStats.totalBodyWeight = this.convertMetricStatToImperial(imperialScanStats.totalBodyWeight);
    return imperialScanStats;
  }

  public static convertCalculatedBodyScaleScanStats(scaleScanStats: any) {
    const { totalBodyWeight, muscleMass } = scaleScanStats;
    const scaleScanStatsInImperial = {
      ...scaleScanStats,
      muscleMass: this.convertMetricStatToImperial(muscleMass),
      totalBodyWeight: this.convertMetricStatToImperial(totalBodyWeight),
    };
    return scaleScanStatsInImperial;
  }

  public static convertGoalTrackingToImperial(goalTrackingStatsInMetric: any): any {
    const { totalBodyWeight: { value: totalBodyWeightVlaue, unit: totalBodyWeightUnit } } = goalTrackingStatsInMetric;
    const { muscleMass: { value: muscleMassVlaue, unit: muscleMassUnit } } = goalTrackingStatsInMetric;
    const goalTrackingStatsInImperial = { ...goalTrackingStatsInMetric };
    if (muscleMassVlaue) {
      goalTrackingStatsInImperial.muscleMass.value = this.convertAmount(
        MassUnit.Killogram,
        MassUnit.Pound,
        muscleMassVlaue,
      );
    }
    if (totalBodyWeightVlaue) {
      goalTrackingStatsInImperial.totalBodyWeight.value = this.convertAmount(
        MassUnit.Killogram,
        MassUnit.Pound,
        totalBodyWeightVlaue,
      );
    }
    if (muscleMassUnit) {
      goalTrackingStatsInImperial.muscleMass.unit = MassUnit.Pound;
    }
    if (totalBodyWeightUnit) {
      goalTrackingStatsInImperial.muscleMass.unit = MassUnit.Pound;
    }
    return goalTrackingStatsInImperial;
  }

  public static convertBodyweightGraphDataToImperial(graphData: any) {
    let graphDataInImperial = { ...graphData };
    const { complete, target, milestone } = graphData;

    if (complete) {
      const completeScans = complete.map((scan: any) => ({
        ...scan,
        value: this.convertAmount(MassUnit.Killogram, MassUnit.Pound, scan.value),
      }));
      graphDataInImperial = {
        ...graphDataInImperial,
        complete: completeScans,
      };
    }
    if (target) {
      graphDataInImperial = {
        ...graphDataInImperial,
        target: {
          ...target,
          value: this.convertAmount(MassUnit.Killogram, MassUnit.Pound, target.value),
        },
      };
    }
    if (milestone) {
      graphDataInImperial = {
        ...graphDataInImperial,
        milestone: {
          ...milestone,
          value: this.convertAmount(MassUnit.Killogram, MassUnit.Pound, milestone.value),
        },
      };
    }

    return graphDataInImperial;
  }

  public static convertWeightToImperial(value: number) {
    return this.convertAmount(MassUnit.Killogram, MassUnit.Pound, value);
  }

  public static convertWeightToMetric(value: number) {
    return this.convertAmount(MassUnit.Pound, MassUnit.Killogram, value);
  }

  public static convertBodyMetricRanges(ranges: any) {
    return this.convertRangeDetails(ranges);
  }

  private static convertMetricStatToImperial(statInMetric: IStat): IStat {
    const {
      rangeValues,
      graphDataPoints,
      graphYAxis,
      changeVal,
      valAndUnit,
      changeUnit,
    } = statInMetric;
    const statInImperial = { ...statInMetric };
    if (rangeValues) {
      statInImperial.rangeValues = this.convertRangeDetails(rangeValues);
    }
    if (graphDataPoints) {
      statInImperial.graphDataPoints = this.convertGraphDataPoints(graphDataPoints);
    }
    if (graphYAxis) {
      statInImperial.graphYAxis = this.convertGraphYAxis(graphYAxis);
    }
    if (changeVal) {
      statInImperial.changeVal = this.convertAmount(
        MassUnit.Killogram,
        MassUnit.Pound,
        changeVal,
      );
    }
    if (valAndUnit) {
      statInImperial.valAndUnit = this.convertValAndUnit(valAndUnit);
    }

    if (changeUnit) {
      statInImperial.changeUnit = MassUnit.Pound;
    }
    return statInImperial;
  }

  private static round(units: number, decimalPlaces: number): number {
    return parseFloat(units.toFixed(decimalPlaces));
  }

  private static convertRangeDetails(input: {
    [x: string]: number;
  }): { [x: string]: number } {
    const newRangeDetails = Object.keys(input).reduce((prev, cur) => ({
      ...prev,
      [cur]: this.convertAmount(
        MassUnit.Killogram,
        MassUnit.Pound,
        input[cur],
      ),
    }), {});
    return newRangeDetails;
  }

  private static convertGraphDataPoints(input: IGraphDataPoints[]): IGraphDataPoints[] {
    const newGraphDataPoints = input.map((item) => {
      if (item.y) {
        return {
          ...item,
          y: this.convertAmount(
            MassUnit.Killogram,
            MassUnit.Pound,
            item.y,
          ),
        };
      }
      return item;
    });
    return newGraphDataPoints;
  }

  private static convertGraphYAxis(input: IGraphYAxis): IGraphYAxis {
    const newMax = this.convertAmount(
      MassUnit.Killogram,
      MassUnit.Pound,
      input.max,
    );
    const newMin = this.convertAmount(
      MassUnit.Killogram,
      MassUnit.Pound,
      input.min,
    );
    return {
      min: newMin,
      max: newMax,
      ticks: input.ticks,
    };
  }

  private static convertValAndUnit(input: string): string {
    let val = parseFloat(input.substring(0, input.length - 2));
    val = this.convertAmount(MassUnit.Killogram, MassUnit.Pound, val);
    return `${val}lb`;
  }

  private static convertInchesAndFeetToTotalFeet(parsedHeightObj: { [key: string]: string }): number {
    const inches = parsedHeightObj.inches ? parseInt(parsedHeightObj.inches, 10) : 0;
    const feet = parsedHeightObj.feet ? parseInt(parsedHeightObj.feet, 10) : 0;
    const totalFeet = Math.round((inches * 0.08333 + feet) * 100) / 100;
    return totalFeet;
  }
}
